import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "@/pages/admin/CandidateForm";
import { DatePicker } from "@/components/DatePicker";
import { api, formatErr, fmtDay } from "@/lib/api";

const BLANK = { title: "", category: "Admin Support", description: "", requirements: "", engagement: "Full-time", hours_per_week: 40,
  rate_min: "", rate_max: "", location: "Remote", timezone: "", start_date: "" };

export const JobFormDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const inp = (k, label, type = "text") => <div className="space-y-1.5"><Label>{label}</Label><Input data-testid={`job-${k}-input`} type={type} value={f[k]} onChange={set(k)} /></div>;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/jobs", { ...f, hours_per_week: Number(f.hours_per_week) || 0, rate_min: Number(f.rate_min) || 0, rate_max: Number(f.rate_max) || 0 });
      toast.success("Job submitted for approval");
      onCreated(data);
      setF(BLANK);
      onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="job-form-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Post a job ad</DialogTitle>
          <DialogDescription>Our team reviews every ad before it goes live to candidates (usually within 24 hours).</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">{inp("title", "Job title")}</div>
          <div className="space-y-1.5"><Label>Category</Label>
            <Select value={f.category} onValueChange={set("category")}><SelectTrigger data-testid="job-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Engagement</Label>
            <Select value={f.engagement} onValueChange={set("engagement")}><SelectTrigger data-testid="job-engagement-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["Full-time", "Part-time", "Project-based"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          {inp("hours_per_week", "Hours / week", "number")}
          <div className="space-y-1.5"><Label>Start date</Label><DatePicker testId="job-start_date-input" value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} /></div>
          {inp("rate_min", "Rate from (USD/hr)", "number")}
          {inp("rate_max", "Rate to (USD/hr)", "number")}
          {inp("location", "Location")}
          {inp("timezone", "Required timezone overlap")}
          <div className="space-y-1.5 sm:col-span-2"><Label>Job description</Label><Textarea data-testid="job-description-input" rows={4} value={f.description} onChange={set("description")} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Requirements (skills, licenses, tools)</Label><Textarea data-testid="job-requirements-input" rows={3} value={f.requirements} onChange={set("requirements")} /></div>
        </div>
        <DialogFooter><Button data-testid="job-submit-button" disabled={busy || !f.title || !f.description} onClick={submit}>{busy ? "Submitting…" : "Submit for approval"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const JobMeta = ({ j }) => (
  <p className="text-xs text-stone-500">
    {[j.category, j.engagement, j.hours_per_week && `${j.hours_per_week} hrs/wk`, (j.rate_min || j.rate_max) && `$${j.rate_min}–${j.rate_max}/hr`, j.location, j.start_date && `Starts ${fmtDay(j.start_date)}`].filter(Boolean).join(" · ")}
  </p>
);
